import React, { useEffect, useMemo, useState } from "react";
import { adminAxios, useAuth } from "../AuthContext";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from "recharts";
import { Loader2, Save, RefreshCw, TrendingUp, DollarSign, Activity, Cpu, ShieldAlert } from "lucide-react";

/**
 * LLM Usage & Budget panel.
 * - SuperAdmin: sees all tenants' usage + can edit global pricing.
 * - Borgo Admin: sees own tenant only, can edit own budget.
 */
export default function LlmUsagePanel() {
    const { user } = useAuth();
    const isSuper = user?.role === "super_admin";

    const [summary, setSummary] = useState(null);
    const [recent, setRecent] = useState([]);
    const [pricing, setPricing] = useState(null);
    const [budget, setBudget] = useState(null);
    const [tenants, setTenants] = useState([]);
    const [selTenant, setSelTenant] = useState("");
    const [days, setDays] = useState(30);
    const [busy, setBusy] = useState(true);
    const [tab, setTab] = useState("overview"); // overview | recent | pricing | budget

    const loadAll = async () => {
        setBusy(true);
        try {
            const [s, r, p, b] = await Promise.all([
                adminAxios().get(`/admin/llm-usage/summary`, { params: { tenant: selTenant || undefined, days } }),
                adminAxios().get(`/admin/llm-usage/recent`, { params: { tenant: selTenant || undefined, limit: 100 } }),
                adminAxios().get(`/admin/llm-usage/pricing`),
                adminAxios().get(`/admin/llm-usage/budget`, { params: selTenant ? { tenant: selTenant } : {} }),
            ]);
            setSummary(s.data);
            setRecent(r.data || []);
            setPricing(p.data);
            setBudget(b.data);
        } catch (e) {
            console.error("llm-usage load", e);
        } finally { setBusy(false); }
    };

    useEffect(() => {
        // SuperAdmin: load tenants list for filter
        if (isSuper) {
            adminAxios().get("/admin/tenants").then((r) => setTenants(r.data || [])).catch(() => {});
        }
    }, [isSuper]);

    useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [selTenant, days]);

    return (
        <div data-testid="admin-llm-usage-panel">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-indigo-700 mb-1">Monitor AI</div>
                    <h2 className="font-serif text-2xl font-semibold text-stone-900">Consumi AI &amp; Budget</h2>
                    <div className="text-xs text-stone-500 mt-1">Traccia chiamate, token e spesa stimata per ogni chiamata AI del sistema.</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {isSuper && (
                        <select
                            value={selTenant}
                            onChange={(e) => setSelTenant(e.target.value)}
                            className="px-3 py-2 rounded-full bg-white border border-stone-200 text-sm"
                            data-testid="llm-usage-tenant"
                        >
                            <option value="">Tutti i borghi</option>
                            {tenants.map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
                        </select>
                    )}
                    <select
                        value={days}
                        onChange={(e) => setDays(parseInt(e.target.value, 10))}
                        className="px-3 py-2 rounded-full bg-white border border-stone-200 text-sm"
                        data-testid="llm-usage-days"
                    >
                        {[7, 14, 30, 60, 90].map((d) => (<option key={d} value={d}>Ultimi {d} giorni</option>))}
                    </select>
                    <button
                        onClick={loadAll}
                        className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold"
                        data-testid="llm-usage-refresh"
                    >
                        <RefreshCw size={13} className={busy ? "animate-spin" : ""} /> Aggiorna
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="mb-5 flex flex-wrap gap-1 border-b border-stone-200">
                {[
                    { id: "overview", label: "Overview", icon: TrendingUp },
                    { id: "recent",   label: "Chiamate recenti", icon: Activity },
                    ...(isSuper ? [{ id: "pricing", label: "Prezzi modelli", icon: DollarSign }] : []),
                    { id: "budget",   label: "Budget", icon: ShieldAlert },
                ].map((t) => {
                    const Icon = t.icon;
                    const on = tab === t.id;
                    return (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${on ? "border-indigo-600 text-indigo-700" : "border-transparent text-stone-500 hover:text-indigo-700"}`}
                            data-testid={`llm-usage-tab-${t.id}`}
                        >
                            <Icon size={13} /> {t.label}
                        </button>
                    );
                })}
            </div>

            {busy && <div className="text-center py-12"><Loader2 size={24} className="animate-spin mx-auto text-indigo-600" /></div>}

            {!busy && tab === "overview" && <OverviewTab summary={summary} pricing={pricing} isSuper={isSuper} />}
            {!busy && tab === "recent"   && <RecentTab rows={recent} />}
            {!busy && tab === "pricing"  && isSuper && <PricingTab pricing={pricing} onSaved={loadAll} />}
            {!busy && tab === "budget"   && <BudgetTab budget={budget} tenants={tenants} isSuper={isSuper} selTenant={selTenant} onSaved={loadAll} />}
        </div>
    );
}


// ============================ OVERVIEW ============================
function KpiCard({ label, value, subtitle, color = "indigo", testid }) {
    const bg = {
        indigo: "from-indigo-50 to-indigo-100 border-indigo-200 text-indigo-700",
        emerald: "from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-700",
        amber: "from-amber-50 to-amber-100 border-amber-200 text-amber-700",
        rose: "from-rose-50 to-rose-100 border-rose-200 text-rose-700",
    }[color];
    return (
        <div className={`rounded-2xl border p-4 bg-gradient-to-br ${bg}`} data-testid={testid}>
            <div className="text-xs font-bold uppercase tracking-wider">{label}</div>
            <div className="font-serif text-3xl font-semibold mt-1 text-stone-900">{value}</div>
            {subtitle && <div className="text-[11px] text-stone-500 mt-0.5">{subtitle}</div>}
        </div>
    );
}

function OverviewTab({ summary, pricing, isSuper }) {
    const modelLabels = useMemo(() => {
        const m = {};
        (pricing?.models || []).forEach((mm) => { m[mm.key] = mm.label; });
        return m;
    }, [pricing]);
    if (!summary) return null;
    const kpi = summary.kpi || {};

    return (
        <div className="space-y-6">
            {/* KPI Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KpiCard label="Chiamate" value={kpi.total_calls?.toLocaleString("it-IT") || 0} subtitle={`ultimi ${summary.range_days}gg`} color="indigo" testid="llm-kpi-calls" />
                <KpiCard label="Token IN"  value={(kpi.total_input || 0).toLocaleString("it-IT")} subtitle="input" color="emerald" testid="llm-kpi-input" />
                <KpiCard label="Token OUT" value={(kpi.total_output || 0).toLocaleString("it-IT")} subtitle="output" color="amber" testid="llm-kpi-output" />
                <KpiCard label="Spesa stimata"  value={`${(kpi.total_cost || 0).toFixed(2)} €`} subtitle="basata sul listino" color="rose" testid="llm-kpi-cost" />
            </div>

            {/* Timeseries chart */}
            <div className="bg-white rounded-2xl border border-stone-200 p-5">
                <div className="flex items-center justify-between mb-3">
                    <div className="font-serif text-lg font-semibold text-stone-900">Andamento giornaliero</div>
                    <div className="text-xs text-stone-500">chiamate (barre) · €/giorno (linea)</div>
                </div>
                {(summary.timeseries || []).length === 0 ? (
                    <div className="text-center text-stone-500 py-12">Nessun dato nel periodo.</div>
                ) : (
                    <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={summary.timeseries}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                            <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                            <YAxis yAxisId="left"  tick={{ fontSize: 11 }} label={{ value: "chiamate", angle: -90, position: "insideLeft", fontSize: 11 }} />
                            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}€`} />
                            <Tooltip formatter={(v, k) => k === "cost" ? `${(+v).toFixed(4)} €` : v} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Bar  yAxisId="left"  dataKey="calls" fill="#4f46e5" name="Chiamate" radius={[6, 6, 0, 0]} />
                            <Line yAxisId="right" type="monotone" dataKey="cost" stroke="#e11d48" strokeWidth={2} dot={{ r: 3 }} name="€" />
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </div>

            {/* Per model + per endpoint */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-white rounded-2xl border border-stone-200 p-5" data-testid="llm-per-model">
                    <div className="font-serif text-lg font-semibold text-stone-900 mb-3">Consumo per modello</div>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-widest text-stone-500 border-b">
                                <th className="text-left py-2">Modello</th>
                                <th className="text-right py-2">Chiamate</th>
                                <th className="text-right py-2">Token IN/OUT</th>
                                <th className="text-right py-2">Spesa</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(summary.per_model || []).map((m) => (
                                <tr key={m.model_key} className="border-b border-stone-100">
                                    <td className="py-2 font-semibold">{modelLabels[m.model_key] || m.model_key}</td>
                                    <td className="text-right tabular-nums">{m.calls.toLocaleString("it-IT")}</td>
                                    <td className="text-right tabular-nums text-stone-600">{(m.tokens_in || 0).toLocaleString("it-IT")} / {(m.tokens_out || 0).toLocaleString("it-IT")}</td>
                                    <td className="text-right tabular-nums font-semibold">{m.cost.toFixed(4)} €</td>
                                </tr>
                            ))}
                            {(summary.per_model || []).length === 0 && <tr><td colSpan="4" className="text-center py-6 text-stone-400">Nessuna chiamata.</td></tr>}
                        </tbody>
                    </table>
                </div>

                <div className="bg-white rounded-2xl border border-stone-200 p-5" data-testid="llm-per-endpoint">
                    <div className="font-serif text-lg font-semibold text-stone-900 mb-3">Consumo per endpoint</div>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-widest text-stone-500 border-b">
                                <th className="text-left py-2">Endpoint</th>
                                <th className="text-right py-2">Chiamate</th>
                                <th className="text-right py-2">Spesa</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(summary.per_endpoint || []).map((m) => (
                                <tr key={m.endpoint} className="border-b border-stone-100">
                                    <td className="py-2 font-semibold">{m.endpoint}</td>
                                    <td className="text-right tabular-nums">{m.calls}</td>
                                    <td className="text-right tabular-nums font-semibold">{m.cost.toFixed(4)} €</td>
                                </tr>
                            ))}
                            {(summary.per_endpoint || []).length === 0 && <tr><td colSpan="3" className="text-center py-6 text-stone-400">Nessuna chiamata.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Per tenant (SuperAdmin only) */}
            {isSuper && (
                <div className="bg-white rounded-2xl border border-stone-200 p-5" data-testid="llm-per-tenant">
                    <div className="font-serif text-lg font-semibold text-stone-900 mb-3">🏆 Top borghi per spesa</div>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-widest text-stone-500 border-b">
                                <th className="text-left py-2">Borgo</th>
                                <th className="text-right py-2">Chiamate</th>
                                <th className="text-right py-2">Spesa</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(summary.per_tenant || []).map((r) => (
                                <tr key={r.tenant_id} className="border-b border-stone-100">
                                    <td className="py-2 font-semibold">{r.borgo_slug || r.tenant_id?.slice(0, 8)}</td>
                                    <td className="text-right tabular-nums">{r.calls}</td>
                                    <td className="text-right tabular-nums font-semibold">{r.cost.toFixed(4)} €</td>
                                </tr>
                            ))}
                            {(summary.per_tenant || []).length === 0 && <tr><td colSpan="3" className="text-center py-6 text-stone-400">Nessun dato.</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}


// ============================ RECENT ============================
function RecentTab({ rows }) {
    return (
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden" data-testid="llm-recent-table">
            <table className="w-full text-xs">
                <thead className="bg-stone-50">
                    <tr className="text-[10px] uppercase tracking-widest text-stone-500 border-b">
                        <th className="text-left px-3 py-2">Quando</th>
                        <th className="text-left px-3 py-2">Endpoint</th>
                        <th className="text-left px-3 py-2">Modello</th>
                        <th className="text-left px-3 py-2">Borgo</th>
                        <th className="text-right px-3 py-2">Token IN</th>
                        <th className="text-right px-3 py-2">Token OUT</th>
                        <th className="text-right px-3 py-2">€</th>
                        <th className="text-right px-3 py-2">Durata</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`llm-recent-row-${i}`}>
                            <td className="px-3 py-2 whitespace-nowrap">{new Date(r.when).toLocaleString("it-IT")}</td>
                            <td className="px-3 py-2 font-semibold">{r.endpoint}</td>
                            <td className="px-3 py-2">{r.model_key}</td>
                            <td className="px-3 py-2">{r.borgo_slug || "—"}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{r.input_tokens}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{r.output_tokens}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-semibold">{(r.cost_eur || 0).toFixed(5)}</td>
                            <td className="px-3 py-2 text-right text-stone-500">{r.duration_ms ? `${r.duration_ms}ms` : "—"}</td>
                        </tr>
                    ))}
                    {rows.length === 0 && <tr><td colSpan="8" className="text-center py-10 text-stone-400">Nessuna chiamata registrata.</td></tr>}
                </tbody>
            </table>
        </div>
    );
}


// ============================ PRICING (SuperAdmin only) ============================
function PricingTab({ pricing, onSaved }) {
    const [rows, setRows] = useState(() => (pricing?.models || []).map((m) => {
        const eff = pricing.effective?.[m.key] || {};
        const def = pricing.defaults?.[m.key] || {};
        return {
            ...m,
            input_per_1m_eur:  eff.input_per_1m_eur  ?? def.input_per_1m_eur  ?? 0,
            output_per_1m_eur: eff.output_per_1m_eur ?? def.output_per_1m_eur ?? 0,
            is_default: (eff.input_per_1m_eur === def.input_per_1m_eur) && (eff.output_per_1m_eur === def.output_per_1m_eur),
        };
    }));
    const [saving, setSaving] = useState("");
    const [msg, setMsg] = useState("");

    const patch = (key, k, v) => setRows((prev) => prev.map((r) => r.key === key ? { ...r, [k]: v, is_default: false } : r));

    const saveOne = async (row) => {
        setSaving(row.key);
        try {
            await adminAxios().put("/admin/llm-usage/pricing", {
                model_key: row.key,
                input_per_1m_eur:  parseFloat(row.input_per_1m_eur)  || 0,
                output_per_1m_eur: parseFloat(row.output_per_1m_eur) || 0,
            });
            setMsg(`Prezzi ${row.label} aggiornati ✓`);
            // Delay parent reload so the toast is visible
            setTimeout(() => { setMsg(""); onSaved?.(); }, 2200);
        } finally { setSaving(""); }
    };

    const resetOne = async (row) => {
        if (!window.confirm(`Ripristinare i prezzi di default per ${row.label}?`)) return;
        setSaving(row.key);
        try {
            await adminAxios().delete(`/admin/llm-usage/pricing/${row.key}`);
            setMsg(`${row.label} → prezzi di default ✓`);
            setTimeout(() => { setMsg(""); onSaved?.(); }, 2200);
        } finally { setSaving(""); }
    };

    return (
        <div className="bg-white rounded-2xl border border-stone-200 p-5" data-testid="llm-pricing-table">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <div className="font-serif text-lg font-semibold text-stone-900">Listino prezzi AI (€ per 1M token)</div>
                    <div className="text-xs text-stone-500 mt-0.5">Solo SuperAdmin. Ogni modifica influenza il calcolo dei costi retroattivo delle chiamate esistenti.</div>
                </div>
                {msg && <div className="text-xs bg-emerald-100 text-emerald-800 px-3 py-1.5 rounded-full font-semibold">{msg}</div>}
            </div>
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-[10px] uppercase tracking-widest text-stone-500 border-b">
                        <th className="text-left py-2">Modello</th>
                        <th className="text-left py-2">Vendor</th>
                        <th className="text-right py-2">Input €/1M</th>
                        <th className="text-right py-2">Output €/1M</th>
                        <th className="text-right py-2">Azioni</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.key} className="border-b border-stone-100" data-testid={`llm-price-row-${r.key}`}>
                            <td className="py-3 font-semibold">
                                <div>{r.label}</div>
                                <div className="text-[10px] text-stone-500 uppercase tracking-wider">{r.tier}</div>
                            </td>
                            <td className="py-3 text-stone-600">{r.vendor}</td>
                            <td className="py-3 text-right">
                                <input type="number" step="0.01" min="0" value={r.input_per_1m_eur}
                                    onChange={(e) => patch(r.key, "input_per_1m_eur", e.target.value)}
                                    className="w-24 px-2 py-1 border border-stone-200 rounded text-right tabular-nums outline-none focus:border-indigo-500"
                                    data-testid={`llm-price-input-${r.key}`}
                                />
                            </td>
                            <td className="py-3 text-right">
                                <input type="number" step="0.01" min="0" value={r.output_per_1m_eur}
                                    onChange={(e) => patch(r.key, "output_per_1m_eur", e.target.value)}
                                    className="w-24 px-2 py-1 border border-stone-200 rounded text-right tabular-nums outline-none focus:border-indigo-500"
                                    data-testid={`llm-price-output-${r.key}`}
                                />
                            </td>
                            <td className="py-3 text-right whitespace-nowrap">
                                <button
                                    onClick={() => saveOne(r)}
                                    disabled={saving === r.key}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-50 mr-1"
                                    data-testid={`llm-price-save-${r.key}`}
                                >
                                    {saving === r.key ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Salva
                                </button>
                                <button
                                    onClick={() => resetOne(r)}
                                    disabled={saving === r.key}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-700 text-xs font-semibold disabled:opacity-50"
                                    data-testid={`llm-price-reset-${r.key}`}
                                >Reset default</button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}


// ============================ BUDGET ============================
function BudgetTab({ budget, tenants, isSuper, selTenant, onSaved }) {
    const [budgetEur, setBudgetEur] = useState(budget?.budget_eur || 0);
    const [hardStop, setHardStop] = useState(!!budget?.hard_stop);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");

    useEffect(() => {
        setBudgetEur(budget?.budget_eur || 0);
        setHardStop(!!budget?.hard_stop);
    }, [budget]);

    const doSave = async () => {
        setBusy(true);
        try {
            const payload = { budget_eur: parseFloat(budgetEur) || 0, hard_stop: hardStop };
            if (isSuper && selTenant) payload.tenant_id = selTenant;
            await adminAxios().put("/admin/llm-usage/budget", payload);
            setMsg("Budget salvato!");
            setTimeout(() => setMsg(""), 2500);
            onSaved?.();
        } catch (e) {
            setMsg("Errore: " + (e?.response?.data?.detail || e.message));
        } finally { setBusy(false); }
    };

    const currentTenantName = isSuper
        ? (tenants.find((t) => t.id === selTenant)?.name || (selTenant ? selTenant.slice(0, 8) : "— seleziona un borgo —"))
        : "il tuo borgo";
    const pct = budget?.pct || 0;
    const overBudget = budget?.has_budget && budget?.spent_eur >= budget?.budget_eur;

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="llm-budget-panel">
            {/* Status */}
            <div className="bg-white rounded-2xl border border-stone-200 p-5">
                <div className="font-serif text-lg font-semibold text-stone-900 mb-3">Stato budget mensile</div>
                {isSuper && !selTenant && (
                    <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                        ⚠️ Seleziona un borgo dal menù in alto per gestirne il budget.
                    </div>
                )}
                {(!isSuper || selTenant) && (
                    <>
                        <div className="text-xs text-stone-500 mb-3">Borgo: <b className="text-stone-800">{currentTenantName}</b></div>
                        {budget?.has_budget ? (
                            <>
                                <div className="flex items-baseline gap-2 mb-2">
                                    <div className="font-serif text-3xl font-bold text-stone-900 tabular-nums" data-testid="llm-budget-spent">{(budget.spent_eur || 0).toFixed(2)} €</div>
                                    <div className="text-sm text-stone-500">/ {(budget.budget_eur || 0).toFixed(2)} €</div>
                                </div>
                                <div className="w-full h-3 rounded-full bg-stone-100 overflow-hidden">
                                    <div
                                        className={`h-full transition-all ${overBudget ? "bg-rose-600" : pct >= 80 ? "bg-amber-500" : "bg-emerald-600"}`}
                                        style={{ width: `${Math.min(100, pct)}%` }}
                                    />
                                </div>
                                <div className="mt-2 text-xs text-stone-600">
                                    {overBudget ? "🚫 Budget esaurito" : `${pct.toFixed(1)}% consumato · ${(budget.remaining_eur || 0).toFixed(2)} € residui`}
                                    {budget.hard_stop && overBudget && " — blocco AI attivo"}
                                </div>
                            </>
                        ) : (
                            <div className="text-sm text-stone-500 py-4">Nessun budget impostato: chiamate AI illimitate.</div>
                        )}
                    </>
                )}
            </div>

            {/* Editor */}
            {(!isSuper || selTenant) && (
                <div className="bg-white rounded-2xl border border-stone-200 p-5">
                    <div className="font-serif text-lg font-semibold text-stone-900 mb-3">Modifica budget</div>
                    <div className="space-y-3">
                        <div>
                            <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider block mb-1">Budget mensile (€)</label>
                            <input
                                type="number" step="0.01" min="0"
                                value={budgetEur}
                                onChange={(e) => setBudgetEur(e.target.value)}
                                placeholder="0 = disattivato"
                                className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm outline-none focus:border-indigo-500"
                                data-testid="llm-budget-input"
                            />
                            <div className="text-[11px] text-stone-500 mt-1">Impostando 0 il budget viene disattivato (chiamate illimitate).</div>
                        </div>
                        <div>
                            <label className="inline-flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={hardStop} onChange={(e) => setHardStop(e.target.checked)} className="w-4 h-4" data-testid="llm-budget-hardstop" />
                                <span className="text-sm text-stone-700"><b>Hard-stop</b>: blocca automaticamente le chiamate AI al superamento</span>
                            </label>
                            <div className="text-[11px] text-stone-500 mt-1">Se disattivato viene mostrato solo un avviso ma le chiamate continuano.</div>
                        </div>
                        {msg && <div className={`text-xs px-3 py-1.5 rounded-full font-semibold inline-block ${msg.startsWith("Errore") ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800"}`}>{msg}</div>}
                        <button
                            onClick={doSave}
                            disabled={busy}
                            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50"
                            data-testid="llm-budget-save"
                        >
                            {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Salva budget
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
